import Vue from 'vue'
import Router from 'vue-router'
import { constantRoutes } from './routes'
import { getToken } from '@/utils/auth'
import store from '@/store'

Vue.use(Router)

// 创建基础路由
const createRouter = () => new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  suppressNavigationErrors: true,
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

// 路由白名单
const whiteList = ['/login', '/404', '/401']

// 动态路由标志
let dynamicRoutesAdded = false

/**
 * 统一角色类型转换
 */
function normalizeRole(role) {
  if (role === null || role === undefined) {
    return null
  }
  
  if (typeof role === 'string') {
    if (role.trim() === '') {
      return null
    }
    if (role.trim() === '0') {
      return 0
    }
    const roleNum = Number(role)
    return isNaN(roleNum) ? role : roleNum
  }
  
  if (typeof role === 'number') {
    return role
  }
  
  console.warn(`⚠️ 未知的角色类型: ${typeof role}, 值: ${role}`)
  return role
}

/**
 * 检查用户是否有权限访问当前路由
 */
function hasPermission(to, userRoles, permissions) {
  // 白名单路由直接放行
  if (whiteList.includes(to.path)) {
    return true
  }
  
  if (!userRoles || userRoles.length === 0) {
    console.log('⚠️ 用户角色为空，暂时允许访问等待后续处理')
    return true
  }
  
  const normalizedUserRoles = userRoles.map(role => normalizeRole(role))
  
  console.log(`🔍 权限检查 - 路由: ${to.path}, 用户角色: ${normalizedUserRoles.join(',')}`)
  
  const matched = to.matched || []
  
  if (matched.length === 0) {
    console.log('❌ 路由匹配失败，可能路由不存在或未添加')
    return false
  }
  
  for (const routeRecord of matched) {
    if (!routeRecord.meta) {
      continue
    }
    
    // 检查角色权限
    if (routeRecord.meta.roles && routeRecord.meta.roles.length) {
      const routeRoles = routeRecord.meta.roles.map(role => normalizeRole(role))
      
      const hasRole = normalizedUserRoles.some(userRole => 
        routeRoles.some(routeRole => {
          const userRoleNum = Number(userRole)
          const routeRoleNum = Number(routeRole)
          
          if (!isNaN(userRoleNum) && !isNaN(routeRoleNum)) {
            return userRoleNum === routeRoleNum
          }
          
          return String(userRole) === String(routeRole)
        })
      )
      
      if (!hasRole) {
        console.log(`⛔ 用户角色 ${normalizedUserRoles.join(',')} 不在允许的角色 ${routeRoles.join(',')} 中`)
        return false
      }
    }
    
    // 检查权限码
    if (routeRecord.meta.permissions && routeRecord.meta.permissions.length) {
      if (!permissions || !permissions.length) {
        return false
      }
      const hasPermission = routeRecord.meta.permissions.some(permission => 
        permissions.includes(permission)
      )
      if (!hasPermission) {
        return false
      }
    }
  }
  
  console.log(`✅ 权限检查通过 - 路由: ${to.path}`)
  return true
}

// 尝试从本地存储恢复用户信息
async function tryRestoreUserInfo() {
  try {
    const cachedUserInfo = localStorage.getItem('user_info')
    const cachedUserRole = localStorage.getItem('user_role')
    const cachedRoles = localStorage.getItem('user_roles')
    
    if (cachedUserInfo) {
      const userInfo = JSON.parse(cachedUserInfo)
      store.commit('user/SET_USER_INFO', userInfo)
      
      let roles = []
      
      if (cachedRoles) {
        try {
          roles = JSON.parse(cachedRoles)
          roles = roles.map(role => normalizeRole(role))
        } catch (e) {
          console.warn('解析缓存的角色列表失败:', e)
        }
      }
      
      if (roles.length === 0 && cachedUserRole) {
        const normalizedRole = normalizeRole(cachedUserRole)
        if (normalizedRole !== null) {
          roles = [normalizedRole]
        }
      }
      
      if (roles.length > 0) {
        store.commit('user/SET_ROLES', roles)
      }
      
      console.log(`✅ 从本地存储恢复用户信息成功，角色: ${roles.join(',')}`)
      return true
    }
    console.log('ℹ️ 本地存储中没有找到用户信息')
    return false
  } catch (error) {
    console.error('恢复用户信息失败:', error)
    return false
  }
}

// 保存用户信息到本地存储
function saveUserInfoToLocalStorage() {
  try {
    const userInfo = store.getters['user/userInfo']
    const roles = store.getters['user/roles']
    
    if (userInfo) {
      localStorage.setItem('user_info', JSON.stringify(userInfo))
      
      if (roles && roles.length > 0) {
        const rolesAsStrings = roles.map(r => String(r))
        localStorage.setItem('user_roles', JSON.stringify(rolesAsStrings))
        localStorage.setItem('user_role', String(roles[0]))
      }
      
      console.log(`✅ 用户信息已保存到本地存储，角色: ${roles ? roles.join(',') : '无'}`)
    }
  } catch (error) {
    console.error('保存用户信息到本地存储失败:', error)
  }
}

// 清除本地存储的用户信息
function clearUserInfoFromLocalStorage() {
  try {
    localStorage.removeItem('user_info')
    localStorage.removeItem('user_role')
    localStorage.removeItem('user_roles')
    console.log('✅ 已清除本地存储的用户信息')
  } catch (error) {
    console.error('清除本地存储用户信息失败:', error)
  }
}

/**
 * 核心修改：确保动态路由已添加 (简化版)
 * 职责：只负责添加路由，不负责导航
 * @returns {Promise<boolean>} 是否添加了新的路由
 */
async function ensureDynamicRoutes() {
  // 如果已添加，直接返回
  if (dynamicRoutesAdded) {
    return false
  }
  
  try {
    let roles = store.getters['user/roles']
    
    // 如果用户角色为空，获取用户信息
    if (!roles || roles.length === 0) {
      console.log('🔄 等待用户信息获取...')
      await store.dispatch('user/getUserInfo')
      roles = store.getters['user/roles']
    }
    
    // 生成动态路由
    console.log(`🔄 生成动态路由... 用户角色: ${roles.join(',')}`)
    const accessRoutes = await store.dispatch('permission/generateRoutes', roles)
    
    if (accessRoutes && accessRoutes.length > 0) {
      // 动态添加到路由器
      router.addRoutes(accessRoutes)
      dynamicRoutesAdded = true
      console.log(`✅ 动态路由添加完成，路由数量: ${accessRoutes.length}`)
      return true // 表示添加了新的路由
    } else {
      console.log('ℹ️ 没有需要添加的动态路由')
      dynamicRoutesAdded = true // 标记为已处理，避免重复检查
      return false
    }
  } catch (error) {
    console.error('确保动态路由失败:', error)
    // 出错也标记，防止死循环
    dynamicRoutesAdded = true
    return false
  }
}



// 全局监听导航结果
router.afterEach((to, from, failure) => {
  if (failure) {
    console.group('🚨 检测到导航失败')
    console.log('失败类型:', failure.type) // 这将明确显示是 'cancelled' 还是其他类型
    console.log('从哪里来:', from.fullPath)
    console.log('本应去向:', to.fullPath)
    console.log('失败详情:', failure)
    console.groupEnd()
  } else {
    // 导航成功时也简单打印，方便对比流程
    console.log(`✅ 导航完成: ${from.path} -> ${to.path}`)
  }
})

// 路由守卫 (逻辑重构，避免重复调用next)
router.beforeEach(async (to, from, next) => {
  console.log(`🚦 路由守卫开始: ${from.path} -> ${to.path}`)
  
  // 设置页面标题
  if (to.meta && to.meta.title) {
    document.title = `${to.meta.title} - 管理系统`
  }

  // 判断是否有 Token
  const hasToken = getToken()
  
  console.log(`🔑 Token状态: ${hasToken ? '存在' : '不存在'}`)
  
  if (hasToken) {
    // 已登录状态下访问登录页，重定向到首页
    if (to.path === '/login') {
      next({ path: '/' })
      return
    }

    try {
      // 检查是否已获取用户信息
      let hasUserInfo = store.getters['user/userInfo']?.id
      let roles = store.getters['user/roles']
      
      console.log(`👤 当前用户信息 - 有用户: ${hasUserInfo}, 角色: ${roles ? roles.join(',') : '无'}`)
      
      // 如果store中没有用户信息，尝试从本地存储恢复
      if (!hasUserInfo || !roles || roles.length === 0) {
        const restored = await tryRestoreUserInfo()
        
        if (restored) {
          hasUserInfo = store.getters['user/userInfo']?.id
          roles = store.getters['user/roles']
          console.log(`✅ 用户信息恢复成功，用户角色: ${roles.join(',')}`)
        } else {
          console.log('ℹ️ 无法从本地存储恢复用户信息，尝试从API获取')
        }
      }
      
      // 如果仍然没有用户信息，则从API获取用户信息
      if (!hasUserInfo || !roles || roles.length === 0) {
        console.log('🔄 从API获取用户信息...')
        await store.dispatch('user/getUserInfo')
        saveUserInfoToLocalStorage()
        
        hasUserInfo = store.getters['user/userInfo']?.id
        roles = store.getters['user/roles']
      }
      
      // 获取权限
      const currentPermissions = store.getters['user/permissions']
      
      console.log(`🔑 最终用户角色: ${roles.join(',')}, 权限: ${currentPermissions ? currentPermissions.join(',') : '无'}`)
      
      // 检查当前路由的访问权限
      const canAccess = hasPermission(to, roles, currentPermissions)
      
      console.log(`🔐 权限检查结果: ${canAccess ? '通过' : '拒绝'}`)
      
      // 无论权限检查是否通过，都需要确保动态路由存在
      if (!dynamicRoutesAdded) {
        console.log('🔄 动态路由未添加，正在处理...')
        const added = await ensureDynamicRoutes()
        console.log('✅ 动态路由处理完成')
        if (added) {
          console.log('🔁 动态路由已新增，使用 replace 重新进入目标路由')
          next({ ...to, replace: true })
          return
        }
      }
      
      // 动态路由添加后，重新匹配路由
      // 注意：添加路由后需要重新获取匹配的路由记录
      const matched = router.getMatchedComponents(to)
      
      // 权限检查不通过
      if (!canAccess) {
        // 重新检查权限（因为动态路由可能已添加）
        const canAccessAfter = hasPermission(to, roles, currentPermissions)
        
        if (!canAccessAfter) {
          // 最终无权限，检查是404还是401
          if (matched.length === 0) {
            console.log(`⚠️ 路由 ${to.path} 不存在，重定向到404`)
            next('/404')
          } else {
            console.log(`⛔ 用户无权访问路由 ${to.path}，用户角色: ${roles.join(',')}`)
            next('/401')
          }
        } else {
          // 重新检查后有权限，放行
          next()
        }
      } else {
        // 权限检查通过，直接放行
        next()
      }
      
    } catch (error) {
      console.error('路由守卫错误:', error)
      
      // 清除本地存储的用户信息
      clearUserInfoFromLocalStorage()
      
      // 检查错误类型
      if (error.response && error.response.status === 401) {
        console.log('🔑 Token可能已过期')
        
        try {
          // 尝试刷新token
          const refreshed = await store.dispatch('user/refreshToken')
          if (refreshed) {
            console.log('✅ Token刷新成功，重试当前路由')
            next({ ...to, replace: true })
            return
          }
        } catch (refreshError) {
          console.error('刷新token失败:', refreshError)
        }
      }
      
      // 清除用户信息并跳转登录页
      await store.dispatch('user/logout')
      next(`/login?redirect=${encodeURIComponent(to.path)}`)
    }
  } else {
    // 没有 Token
    if (whiteList.includes(to.path)) {
      next()
    } else {
      next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
    }
  }
})

// 路由守卫错误处理
router.onError((error) => {
  console.error('路由加载错误:', error)
  
  const pattern = /Loading chunk (\d)+ failed/g
  const isChunkLoadFailed = error.message.match(pattern)
  
  if (isChunkLoadFailed) {
    const targetPath = router.history.pending.fullPath
    if (targetPath) {
      window.location.href = targetPath
    }
  }
})

// 监听用户信息变化
store.subscribe((mutation) => {
  if (mutation.type.startsWith('user/')) {
    const userMutations = [
      'user/SET_USER_INFO',
      'user/SET_ROLES',
      'user/SET_PERMISSIONS'
    ]
    
    if (userMutations.includes(mutation.type)) {
      setTimeout(() => {
        saveUserInfoToLocalStorage()
      }, 100)
    }
  }
})

// 重置路由
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
  dynamicRoutesAdded = false
  store.commit('permission/RESET_ROUTES')
}

// 初始化函数，在应用启动时调用
export async function initRouterOnRefresh() {
  const hasToken = getToken()
  
  if (hasToken) {
    try {
      console.log('🔄 页面刷新，初始化路由...')
      
      // 恢复用户信息
      const restored = await tryRestoreUserInfo()
      
      if (restored) {
        const roles = store.getters['user/roles']
        console.log(`✅ 用户信息恢复成功，用户角色: ${roles.join(',')}`)
        
        // 立即生成并添加动态路由
        if (!dynamicRoutesAdded) {
          console.log('🔄 页面刷新时生成动态路由...')
          const accessRoutes = await store.dispatch('permission/generateRoutes', roles)
          
          if (accessRoutes && accessRoutes.length > 0) {
            router.addRoutes(accessRoutes)
            dynamicRoutesAdded = true
            console.log('✅ 页面刷新时动态路由添加完成')
          }
        }
      } else {
        console.log('ℹ️ 页面刷新时未找到缓存的用户信息')
      }
    } catch (error) {
      console.error('页面刷新初始化路由失败:', error)
    }
  } else {
    console.log('ℹ️ 页面刷新时未检测到Token')
  }
}

export default router
